import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2 } from "lucide-react";
import { CategoryFields, CategoryFieldsValue, NO_PARENT } from "./CategoryFields";
import type { AdminCategory } from "./ServiceCategoriesTab";

const API_URL = process.env.REACT_APP_API_URL || "";

const EMPTY: CategoryFieldsValue = {
  key: "",
  nameEt: "",
  nameEn: "",
  nameRu: "",
  icon: "Wrench",
  parentId: NO_PARENT,
  sortOrder: "0",
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  topLevel: AdminCategory[];
  onSaved: () => void;
}

export function AddCategoryDialog({ open, onOpenChange, topLevel, onSaved }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CategoryFieldsValue>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setForm(EMPTY);
    setError("");
  };

  const patch = (p: Partial<CategoryFieldsValue>) => setForm((prev) => ({ ...prev, ...p }));

  const handleSubmit = async () => {
    setError("");
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/service-categories`,
        {
          key: form.key.trim(),
          nameEt: form.nameEt || null,
          nameEn: form.nameEn || null,
          nameRu: form.nameRu || null,
          icon: form.icon,
          parentId: form.parentId === NO_PARENT ? null : form.parentId,
          sortOrder: Number(form.sortOrder) || 0,
        },
        { withCredentials: true }
      );
      reset();
      onOpenChange(false);
      onSaved();
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.code === "DUPLICATE_KEY") setError(t("admin.categories.duplicateKey"));
      else setError(data?.error || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    !!form.key.trim() && !!(form.nameEt || form.nameEn || form.nameRu) && !!form.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("admin.categories.addCategory")}</DialogTitle>
        </DialogHeader>
        <CategoryFields value={form} onChange={patch} keyEditable topLevel={topLevel} />
        {error && (
          <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg border border-destructive/20">
            {error}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !canSubmit}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
